class CurrencyConverter {
  constructor(options) {
    this.apiKey = options.apiKey; // untuk provider fallback ExchangeRate-API
    this.baseCurrency = options.baseCurrency;
    this.targets = options.targets;
    this.cacheHours = options.cacheHours || 12;
    this.fallbackRates = options.fallbackRates || {};

    // storageKey include daftar target currency, supaya cache OTOMATIS basi
    // kalau kamu nambah/kurang currency di theme settings (misal nambah EUR).
    const targetsKey = [...this.targets].sort().join('-');
    this.storageKey = `currency_rates_${this.baseCurrency}_${targetsKey}`;

    this.selectedCurrency =
      localStorage.getItem('display_currency') || this.baseCurrency;
    this.rates = {};
    this.ready = false;
    this.rateSource = null; // tracking asal data untuk logging

    this.init();
  }

  async init() {
    this.rates = await this.getRates();
    this.ready = true;
    this.logRates();
    this.convertAllPrices();
    this.bindSwitcher();
    this.observeDynamicContent();
  }

  // --- Ambil rates dengan strategi cache-first + fallback chain ---
  async getRates() {
    const cached = this.readCache();
    if (cached) {
      this.rateSource = 'cache';
      return cached;
    }

    const fresh = await this.fetchWithFallbackChain();
    if (fresh) {
      this.writeCache(fresh);
      return fresh;
    }

    const stale = this.readCache(true); // izinkan expired
    if (stale) {
      this.rateSource = 'stale-cache';
      return stale;
    }

    this.rateSource = 'fallback-manual';
    return this.fallbackRates;
  }

  // --- Coba tiap provider berurutan, berhenti begitu ada yang sukses ---
  async fetchWithFallbackChain() {
    const providers = [
      { name: 'fetch-jsdelivr', fn: () => this.fetchFawazahmed0Primary() },
      { name: 'fetch-cloudflare-mirror', fn: () => this.fetchFawazahmed0Mirror() },
      { name: 'fetch-exchangerate-api', fn: () => this.fetchExchangeRateAPI() },
    ];

    for (const provider of providers) {
      try {
        const result = await provider.fn();
        if (result && Object.keys(result).length > 0) {
          this.rateSource = provider.name;
          return result;
        }
      } catch (err) {
        console.warn(`[CurrencyConverter] Provider "${provider.name}" gagal:`, err.message);
      }
    }
    return null;
  }

  async fetchJson(url, timeoutMs = 5000) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } finally {
      clearTimeout(timeout);
    }
  }

  // Provider 1: fawazahmed0 via jsDelivr CDN (utama, no rate limit)
  async fetchFawazahmed0Primary() {
    const base = this.baseCurrency.toLowerCase();
    const url = `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/${base}.json`;
    const data = await this.fetchJson(url);
    return this.extractFawazahmed0Rates(data, base);
  }

  // Provider 2: fawazahmed0 via Cloudflare Pages (mirror resmi)
  async fetchFawazahmed0Mirror() {
    const base = this.baseCurrency.toLowerCase();
    const url = `https://latest.currency-api.pages.dev/v1/currencies/${base}.json`;
    const data = await this.fetchJson(url);
    return this.extractFawazahmed0Rates(data, base);
  }

  extractFawazahmed0Rates(data, base) {
    const allRates = data[base];
    if (!allRates) throw new Error('Format response tidak sesuai (key base tidak ditemukan)');
    const filtered = {};
    this.targets.forEach((code) => {
      const lower = code.toLowerCase();
      if (allRates[lower] !== undefined) filtered[code] = allRates[lower];
    });
    return filtered;
  }

  // Provider 3: ExchangeRate-API (pakai key, sebagai secondary/fallback)
  async fetchExchangeRateAPI() {
    if (!this.apiKey) throw new Error('API key tidak diset');
    const url = `https://v6.exchangerate-api.com/v6/${this.apiKey}/latest/${this.baseCurrency}`;
    const data = await this.fetchJson(url);
    if (data.result !== 'success') throw new Error('Response gagal: ' + (data['error-type'] || 'unknown'));

    const filtered = {};
    this.targets.forEach((code) => {
      if (data.conversion_rates && data.conversion_rates[code] !== undefined) {
        filtered[code] = data.conversion_rates[code];
      }
    });
    return filtered;
  }

  // --- Cache ---
  readCache(ignoreExpiry = false) {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return null;
      const { rates, timestamp } = JSON.parse(raw);
      const ageHours = (Date.now() - timestamp) / (1000 * 60 * 60);
      if (!ignoreExpiry && ageHours > this.cacheHours) return null;
      return rates;
    } catch {
      return null;
    }
  }

  writeCache(rates) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({ rates, timestamp: Date.now() }));
    } catch (err) {
      console.warn('[CurrencyConverter] Gagal menyimpan cache:', err.message);
    }
  }

  // --- Format & convert ---
  // formatMoney(amount, currency) {
  //   try {
  //     return new Intl.NumberFormat(undefined, {
  //       style: 'currency',
  //       currency,
  //       minimumFractionDigits: currency === 'IDR' ? 0 : 2,
  //     }).format(amount);
  //   } catch {
  //     return `${currency} ${amount.toFixed(2)}`;
  //   }
  // }
  formatMoney(amount, currency) {
    // Override manual untuk currency yang butuh format custom
    const customFormats = {
      AUD: (val) => `AUD $${val.toFixed(2)}`,
    };

    if (customFormats[currency]) {
      return customFormats[currency](amount);
    }

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: currency === 'IDR' ? 0 : 2,
      }).format(amount);
    } catch {
      return `${currency} ${amount.toFixed(2)}`;
    }
}
    roundPrice(amount, currency) {
       return Math.ceil(amount);
    // const integer = Math.floor(amount);
    // const decimal = amount - integer;

    // switch (currency) {
    //   case 'USD': {
    //     // Selalu naik ke .00 atau .95
    //     if (decimal === 0) return amount;

    //     if (decimal <= 0.95) {
    //       return integer + 0.95;
    //     }

    //     return integer + 1;
    //   }

    //   case 'EUR': {
    //     // Selalu naik ke .90, .95 atau .00
    //     if (decimal === 0) return amount;

    //     if (decimal <= 0.90) {
    //       return integer + 0.90;
    //     }

    //     if (decimal <= 0.95) {
    //       return integer + 0.95;
    //     }

    //     return integer + 1;
    //   }

    //   default:
    //     return amount;
    // }
  }

  convertAllPrices() {
    if (!this.ready) return;

    document.querySelectorAll('[data-original-price-cents]').forEach((el) => {
      const cents = parseFloat(el.dataset.originalPriceCents);
      if (isNaN(cents)) return;
      const baseAmount = cents / 100;

      if (this.selectedCurrency === this.baseCurrency) {
        el.textContent = this.formatMoney(baseAmount, this.baseCurrency);
        return;
      }

      const rate = this.rates[this.selectedCurrency];
      if (!rate) {
        el.textContent = this.formatMoney(baseAmount, this.baseCurrency);
        return;
      }
      let converted = baseAmount * rate;

      converted = this.roundPrice(converted, this.selectedCurrency);

      el.textContent = this.formatMoney(
        converted,
        this.selectedCurrency
      );
      // el.textContent = this.formatMoney(baseAmount * rate, this.selectedCurrency);
    });
  }

  bindSwitcher() {
    const select = document.getElementById('CurrencySwitcher');
    if (!select) return;
    select.value = this.selectedCurrency;
    select.addEventListener('change', (e) => {
      this.selectedCurrency = e.target.value;
      localStorage.setItem('display_currency', this.selectedCurrency);
      this.convertAllPrices();
    });
  }

  
  observeDynamicContent() {
    let debounceTimer;
    const debouncedConvert = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => this.convertAllPrices(), 100);
    };

    ['CartDrawer', 'MainContent', 'shopify-section-cart-items'].forEach((id) => {
      const target = document.getElementById(id);
      if (target) {
        new MutationObserver(debouncedConvert).observe(target, {
          childList: true,
          subtree: true,
        });
      }
    });

    document.addEventListener('cart:updated', debouncedConvert);
    document.addEventListener('cart:refresh', debouncedConvert);
  }

  // --- Debug logging ---
  logRates() {
    const sourceLabels = {
      'cache': '🟡 CACHE (localStorage, belum expired)',
      'fetch-jsdelivr': '🟢 FETCH BARU (jsDelivr CDN)',
      'fetch-cloudflare-mirror': '🟢 FETCH BARU (Cloudflare Pages mirror)',
      'fetch-exchangerate-api': '🟢 FETCH BARU (ExchangeRate-API)',
      'stale-cache': '🟠 STALE CACHE (expired, dipakai karena semua fetch gagal)',
      'fallback-manual': '🔴 FALLBACK MANUAL (dari theme settings, semua sumber gagal)',
    };

    console.group(
      '%c[CurrencyConverter] Rates loaded',
      'color: #16a34a; font-weight: bold; font-size: 12px;'
    );

    console.log('Sumber data:', sourceLabels[this.rateSource] || this.rateSource);
    console.log('Base currency (toko):', this.baseCurrency);
    console.log('Target currencies (dari settings):', this.targets);
    console.log('Raw rates object:', this.rates);

    console.log('--- Konversi per currency ---');
    this.targets.forEach((code) => {
      const rate = this.rates[code];
      if (rate === undefined || rate === null) {
        console.warn(
          `⚠️ Rate untuk ${code} TIDAK ADA di data. Kemungkinan currency ini tidak didukung provider, atau cache lama belum ter-refresh.`
        );
        return;
      }
      const idrPerUnit = 1 / rate;
      console.log(
        `1 ${this.baseCurrency} = ${rate} ${code}   |   1 ${code} = ${idrPerUnit.toLocaleString('id-ID', { maximumFractionDigits: 2 })} ${this.baseCurrency}`
      );
    });

    console.groupEnd();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (!window.currencyConverterSettings?.enabled) return;
  window.currencyConverterInstance = new CurrencyConverter(window.currencyConverterSettings);
});